































































































import {
  CommentModel,
  IComment,
  ICustomer,
  IDamage,
  IPolicy,
  ITask,
  IUser,
  UserStatus,
} from 'admiral-core';
import { mixins } from 'vue-class-component';
import { Component, Prop, Watch } from 'vue-property-decorator';
import { BaseMixin } from '@/mixins/BaseMixin';
import Avatar from './Avatar.vue';
import { CommentApi } from '@/services/CommentApi';
import { FormMixin } from '@/mixins/FormMixin';
import { Util } from '@/utils/Util';
import { TrashIcon } from 'vue-feather-icons';

/** Maximale Höhe des @-Mention-Dropdowns in px (muss zur CSS-max-height passen). */
const MENTION_DROPDOWN_MAX_HEIGHT = 240;

@Component({
  components: {
    Avatar,
    TrashIcon,
  },
})
export default class CommentBox extends mixins(BaseMixin, FormMixin) {
  @Prop({ required: false }) policy!: IPolicy;
  @Prop({ required: false }) task!: ITask;
  @Prop({ required: false }) damage!: IDamage;
  @Prop({ required: false }) customer!: ICustomer;

  value: CommentModel = this.resetComment();

  comments: IComment[] = [];
  currentSubscriptionTopic: string | null = null;

  // @mention State
  mentionDropdownVisible = false;
  mentionSearch = '';
  mentionSelectedIdx = 0;
  mentionStartPos = -1;
  mentionOpenUp = false; // true → Dropdown öffnet nach oben (wenn unten zu wenig Platz)

  async mounted() {
    await this.loadComments();
    this.subscribeToEntityComments();
    this.$nextTick(() => this.autoResizeTextarea());
  }

  beforeDestroy() {
    this.unsubscribeFromEntityComments();
  }

  @Watch('policy')
  async policyChanged() {
    this.unsubscribeFromEntityComments();
    if (!this.policy) {
      return;
    }
    await this.loadComments();
    this.subscribeToEntityComments();
  }

  @Watch('task')
  async taskChanged() {
    this.unsubscribeFromEntityComments();
    if (!this.task) {
      return;
    }
    await this.loadComments();
    this.subscribeToEntityComments();
  }

  @Watch('damage')
  async damageChanged() {
    this.unsubscribeFromEntityComments();
    if (!this.damage) {
      return;
    }
    await this.loadComments();
    this.subscribeToEntityComments();
  }

  @Watch('customer')
  async customerChanged() {
    this.unsubscribeFromEntityComments();
    if (!this.customer) {
      return;
    }
    await this.loadComments();
    this.subscribeToEntityComments();
  }

  get isNewEntity() {
    return !this.policy?.id && !this.task?.id && !this.damage?.id && !this.customer?.id;
  }

  // ── @mention ──────────────────────────────────────────────
  // Quelle ist this.users (bereits mandantenfähig auf die eigene Agentur geladen).

  /** Aktive Mitarbeiter der eigenen Agentur (erwähnbar inkl. sich selbst). */
  get activeUsers(): IUser[] {
    return (this.users || []).filter((u) => u.status === UserStatus.ACTIVE);
  }

  get filteredUsers(): IUser[] {
    if (!this.mentionSearch) return this.activeUsers;
    const q = this.mentionSearch.toLowerCase();
    return this.activeUsers.filter((u) =>
      `${u.firstname} ${u.lastname}`.toLowerCase().includes(q),
    );
  }

  onTextareaInput(event: Event) {
    this.autoResizeTextarea();
    const el = event.target as HTMLTextAreaElement;
    const cursor = el.selectionStart ?? 0;
    const text = el.value;

    // Rückwärts vom Cursor bis '@' oder Leerzeichen/Zeilenumbruch suchen
    let i = cursor - 1;
    while (i >= 0 && text[i] !== ' ' && text[i] !== '\n') {
      if (text[i] === '@') {
        this.mentionStartPos = i;
        this.mentionSearch = text.substring(i + 1, cursor);
        this.mentionSelectedIdx = 0;
        this.mentionDropdownVisible = true;
        this.updateMentionDirection();
        return;
      }
      i--;
    }
    this.mentionDropdownVisible = false;
  }

  /**
   * Entscheidet, ob das Dropdown nach unten (Standard) oder nach oben öffnet:
   * nach oben nur, wenn unterhalb des Felds zu wenig Platz ist und oberhalb mehr.
   */
  updateMentionDirection() {
    const el = this.$refs.commentInput as HTMLTextAreaElement | undefined;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    this.mentionOpenUp = spaceBelow < MENTION_DROPDOWN_MAX_HEIGHT && spaceAbove > spaceBelow;
  }

  onTextareaKeydown(event: KeyboardEvent) {
    if (!this.mentionDropdownVisible) {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        this.submitComment();
      }
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.mentionSelectedIdx = Math.min(
        this.mentionSelectedIdx + 1,
        this.filteredUsers.length - 1,
      );
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.mentionSelectedIdx = Math.max(this.mentionSelectedIdx - 1, 0);
    } else if (event.key === 'Enter' || event.key === 'Tab') {
      event.preventDefault();
      if (this.filteredUsers[this.mentionSelectedIdx]) {
        this.selectMention(this.filteredUsers[this.mentionSelectedIdx]);
      }
    } else if (event.key === 'Escape') {
      this.mentionDropdownVisible = false;
    }
  }

  selectMention(u: IUser) {
    const el = this.$refs.commentInput as HTMLTextAreaElement;
    const text = el.value;
    const before = text.substring(0, this.mentionStartPos);
    const after = text.substring(el.selectionStart ?? this.mentionStartPos);
    const mention = `@${u.firstname} ${u.lastname}`;

    this.value.text = `${before}${mention} ${after}`;
    this.mentionDropdownVisible = false;

    // Cursor hinter die Erwähnung setzen
    this.$nextTick(() => {
      const pos = before.length + mention.length + 1;
      el.setSelectionRange(pos, pos);
      el.focus();
      this.autoResizeTextarea();
    });
  }

  hideMentionDropdownDelayed() {
    setTimeout(() => {
      this.mentionDropdownVisible = false;
    }, 150);
  }

  autoResizeTextarea() {
    const el = this.$refs.commentInput as HTMLTextAreaElement;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.max(45, el.scrollHeight)}px`;
  }

  /** Wandelt Klartext in HTML um und markiert echte Mitarbeiternamen (aktiv, eigene Agentur). */
  private highlightNames(text: string, className: string): string {
    const escaped = (text || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

    const names = this.activeUsers
      .map((u) => `${u.firstname} ${u.lastname}`.trim())
      .filter((n) => n.length > 0)
      // längere Namen zuerst, damit Teilnamen nicht vorher matchen
      .sort((a, b) => b.length - a.length);

    if (names.length === 0) return escaped;

    const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const pattern = new RegExp(`@(${names.map(escapeRegExp).join('|')})(?![\\p{L}\\-])`, 'gu');
    return escaped.replace(pattern, `<span class="${className}">@$1</span>`);
  }

  /** Hervorhebung in bereits geposteten Kommentaren. */
  renderMentions(text: string): string {
    return this.highlightNames(text, 'mention-highlight');
  }

  /** Live-Hervorhebung im Eingabefeld – rendert im Backdrop hinter dem transparenten Textarea. */
  get highlightedInput(): string {
    const html = this.highlightNames(this.value.text || '', 'mention-input-hl');
    // abschließenden Zeilenumbruch sichtbar halten, damit die Backdrop-Höhe zum Textarea passt
    return html.endsWith('\n') ? `${html}&nbsp;` : html;
  }

  // ── Submit ────────────────────────────────────────────────

  async submitComment() {
    if (!this.value.text?.trim()) return;

    if (this.isNewEntity) {
      // Keine Entity-Referenz anhängen — sonst zirkuläre JSON-Struktur über model.newComments.
      this.comments.push({
        id: 'new-comment-' + this.comments.length,
        text: this.value.text || '',
        user: this.user!,
        createdAt: new Date(),
        updatedAt: new Date(),
        isDemo: false,
      });
      this.$emit('new-comments', this.comments);
      this.scrollToTop();
      this.value = this.resetComment();
      this.$nextTick(() => this.autoResizeTextarea());
      return;
    }

    try {
      await CommentApi.create({
        policyId: this.policy?.id,
        taskId: this.task?.id,
        damageId: this.damage?.id,
        customerId: this.customer?.id,
        text: this.value.text || '',
      });
      this.value = this.resetComment();
      this.$nextTick(() => this.autoResizeTextarea());
      await this.loadComments();
      this.scrollToTop();
    } catch (error) {
      //
    }
  }

  async loadComments() {
    if (this.isNewEntity) {
      return;
    }
    const response = await CommentApi.get({
      policyId: this.policy?.id,
      taskId: this.task?.id,
      damageId: this.damage?.id,
      customerId: this.customer?.id,
    });
    this.comments = response.data;
    this.notifyCommentsCount();
  }

  /** Meldet die aktuelle Kommentaranzahl an übergeordnete Views (z. B. Tab-Badge). */
  notifyCommentsCount(): void {
    this.$emit('comments-count-changed', this.comments.length);
  }

  resetComment() {
    return {
      text: '',
      policy: this.policy,
      task: this.task,
      damage: this.damage,
      customer: this.customer,
      user: this.user!,
    };
  }

  formatDate(date: Date) {
    return Util.formatDate(date);
  }

  formatDateFull(date: Date) {
    return Util.formatDateFull(date);
  }

  async deleteComment(id: string) {
    if (this.isNewEntity) {
      this.comments = this.comments.filter((comment) => comment.id !== id);
      this.notifyCommentsCount();
      return;
    }
    try {
      await CommentApi.delete(id);
    } catch (error) {
      //
    }
    await this.loadComments();
  }

  scrollToTop() {
    this.$nextTick(() => {
      const overview = this.$refs.commentsOverview as HTMLElement | undefined;
      if (!overview) {
        return;
      }
      overview.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    });
  }
  canDeleteComment(comment: IComment) {
    return this.user?.id === comment.user.id || this.hasAdminPermissions;
  }

  getEntityTopic(): string | null {
    if (this.policy?.id) {
      return `policy-comments-${this.policy.id}`;
    }
    if (this.task?.id) {
      return `task-comments-${this.task.id}`;
    }
    if (this.damage?.id) {
      return `damage-comments-${this.damage.id}`;
    }
    if (this.customer?.id) {
      return `customer-comments-${this.customer.id}`;
    }
    return null;
  }

  subscribeToEntityComments() {
    const topic = this.getEntityTopic();
    if (!topic) {
      return;
    }

    // Only subscribe if not already subscribed to this topic
    if (this.currentSubscriptionTopic === topic) {
      return;
    }

    this.currentSubscriptionTopic = topic;

    this.$store.dispatch('socketConnection/subscribeToTopic', {
      topic: topic,
      eventName: 'commentUpdated',
      handler: (data: any) => {
        // Reload comments when a new comment is created/updated
        this.loadComments();
      },
    });
  }

  unsubscribeFromEntityComments() {
    if (!this.currentSubscriptionTopic) {
      return;
    }

    this.$store.dispatch('socketConnection/unsubscribeFromTopic', {
      topic: this.currentSubscriptionTopic,
      eventName: 'commentUpdated',
    });

    this.currentSubscriptionTopic = null;
  }
}
